import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, Search, Loader2, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import GuardedDialog from '../../components/GuardedDialog';
import useFormDirtyState from '../../hooks/useFormDirtyState';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import DashboardWrapper from '../../components/DashboardWrapper';
import ProductForm from '../../components/forms/ProductForm';
import DistributionAttachForm from '../../components/forms/DistributionAttachForm';
import AdminOriginalPublisherManager from '../../components/admin/AdminOriginalPublisherManager';
import AdminDistributionsManager from '../../components/admin/AdminDistributionsManager';
import api from '../../lib/api';
import { toast } from 'sonner';
import resolveProduct from '../../domain/product/resolveProduct';

/**
 * PartnerProducts V3 - Utilise le composant unifié ProductForm
 * 
 * REFACTORISÉ pour:
 * - Utiliser ProductForm comme source unique de vérité UI
 * - Masquer automatiquement les champs Admin (tags, sélection du mois, etc.)
 * - Même expérience utilisateur que Admin avec moins de champs
 * - Zéro régression fonctionnelle
 */

// Structure initiale du formulaire alignée avec le backend
const getInitialFormData = () => ({
  // 1. IDENTIFICATION
  intitule: '',
  code_ean: '',
  
  // 2. DATES
  date_sortie_iso: '',
  
  // 3. PRICING
  prix_vente_conseille: '',
  tva: '',
  
  // 4. PRÉSENTATION
  auteur: '',
  illustrateur: '',
  description_courte: '',
  descriptif: '',
  liste_materiel: '',
  
  // 5. CARACTÉRISTIQUES
  nb_joueurs_min: '',
  nb_joueurs_max: '',
  age_minimum: '',
  temps_moyen_partie: '',
  type_jeu: '',
  public_cible: '',
  tags_produit_ids: [],
  langue: '',
  pays_provenance: '',
  
  // 6. MÉDIAS & MARKETING
  visuels: [],
  atouts_1: '',
  atouts_2: '',
  atouts_3: '',
  pourquoi_referencer: '',
  plan_communication_commercial: '',
  video_url: '',
  regle_jeu_url: '',
  
  // 7. LOGISTIQUE
  taille_boite: '',
  poids_boite: '',
  
  // 8. SYSTÈME
  is_active: true
});

const PartnerProducts = () => {
  const { user, activeOrg, hasPermission } = useAuth();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState(getInitialFormData());
  const { hasUnsavedChanges, setDirty, resetDirty } = useFormDirtyState();
  const [pendingPublishers, setPendingPublishers] = useState([]);
  const [pendingDistributions, setPendingDistributions] = useState([]);
  const distManagerRef = useRef(null);

  // Phase 4.5b — scope par l'organisation active (OrgSwitcher) et non plus par user.partner_id
  const partnerId = activeOrg?.org_id;
  // Permissions granulaires
  const canEditProducts = hasPermission('products.edit');
  const canCreateProducts = hasPermission('products.create');
  
  // Partner scopé comme unique choix dans le dropdown distributions
  const partnerAsArray = useMemo(() => {
    if (!partnerId) return [];
    return [{ id: partnerId, nom: activeOrg?.org_nom || user?.nom || 'Mon entreprise', is_active: true }];
  }, [partnerId, activeOrg, user]);

  useEffect(() => {
    const controller = new AbortController();
    if (partnerId) {
      loadProducts(controller.signal);
    } else {
      setProducts([]);
      setLoading(false);
    }
    return () => controller.abort();
  }, [partnerId]);

  const loadProducts = async (signal) => {
    if (!partnerId) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/products?partner_id=${partnerId}`, { signal });
      const data = response.data;
      setProducts(Array.isArray(data) ? data : (data?.products || []));
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
      console.error('Error loading products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage local des produits
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;
    
    const term = searchTerm.toLowerCase();
    return products.filter(product =>
      product.intitule?.toLowerCase().includes(term) ||
      resolveProduct(product).editors.displayLabel?.toLowerCase().includes(term) ||
      product.code_ean?.includes(term) ||
      product.ref_distributeur?.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setPendingDistributions([]);
      setFormData({
        // 1. IDENTIFICATION
        intitule: product.intitule || '',
        code_ean: product.code_ean || '',
        ref_distributeur: product.ref_distributeur || '',
        
        // 2. PRÉSENTATION
        auteur: product.auteur || '',
        illustrateur: product.illustrateur || '',
        description_courte: product.description_courte || '',
        descriptif: product.descriptif || '',
        liste_materiel: product.liste_materiel || '',
        
        // 3. CARACTÉRISTIQUES
        nb_joueurs_min: product.nb_joueurs_min ?? '',
        nb_joueurs_max: product.nb_joueurs_max ?? '',
        age_minimum: product.age_minimum ?? '',
        temps_moyen_partie: product.temps_moyen_partie ?? '',
        type_jeu: product.type_jeu || '',
        public_cible: product.public_cible || '',
        mecanisme: product.mecanisme || '',  // Legacy — lecture seule, non affiché
        tags_produit_ids: product.tags_produit_ids || [],
        langue: product.langue || '',
        pays_provenance: product.pays_provenance || '',
        
        // 4. PRICING
        prix_vente_conseille: product.prix_vente_conseille ?? '',
        tva: product.tva ?? '',
        
        // 5. LOGISTIQUE
        taille_boite: product.taille_boite || '',
        poids_boite: product.poids_boite ?? '',
        
        // 6. DATES
        date_sortie_iso: (product.date_sortie_iso || '').slice(0, 10),
        // disponibilite: SUPPRIMÉ → distributions
        
        // 7. MARKETING
        visuels: product.visuels || [],
        atouts_1: product.atouts_1 || '',
        atouts_2: product.atouts_2 || '',
        atouts_3: product.atouts_3 || '',
        plan_communication_commercial: product.plan_communication_commercial || '',
        video_url: product.video_url || '',
        regle_jeu_url: product.regle_jeu_url || '',
        
        // 8. SYSTÈME
        is_active: product.is_active ?? true
      });
    } else {
      setEditingProduct(null);
      setFormData(getInitialFormData());
      setPendingDistributions([]);
    }
    setShowDialog(true);
    resetDirty();
  };

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setDirty();
  }, [setDirty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.intitule.trim()) {
      toast.error('Le nom du produit est obligatoire');
      return;
    }
    // Phase 2.2: Validations champs obligatoires
    if (!formData.code_ean?.trim()) {
      toast.error('Le code EAN est obligatoire');
      return;
    }
    if (!formData.descriptif?.trim()) {
      toast.error('La description complète est obligatoire');
      return;
    }
    
    setSaving(true);
    
    try {
      // Préparer les données pour l'API
      const submitData = {
        // 1. IDENTIFICATION
        intitule: formData.intitule,
        code_ean: formData.code_ean || null,
        
        // D3: envoie activeOrg.org_id — le backend valide via resolve_write_org_id
        partner_id: partnerId || undefined,
        
        // 2. PRÉSENTATION
        auteur: formData.auteur || null,
        illustrateur: formData.illustrateur || null,
        description_courte: formData.description_courte || null,
        descriptif: formData.descriptif || '',
        liste_materiel: formData.liste_materiel || null,
        
        // 3. CARACTÉRISTIQUES
        nb_joueurs_min: formData.nb_joueurs_min !== '' ? parseInt(formData.nb_joueurs_min) : null,
        nb_joueurs_max: formData.nb_joueurs_max !== '' ? parseInt(formData.nb_joueurs_max) : null,
        age_minimum: formData.age_minimum !== '' ? parseInt(formData.age_minimum) : null,
        temps_moyen_partie: formData.temps_moyen_partie !== '' ? parseInt(formData.temps_moyen_partie) : null,
        type_jeu: formData.type_jeu || null,
        public_cible: formData.public_cible || null,
        mecanisme: formData.mecanisme || null,
        tags_produit_ids: formData.tags_produit_ids || [],
        langue: formData.langue || null,
        pays_provenance: formData.pays_provenance || null,
        
        // 4. PRICING
        // prix_achat: SUPPRIMÉ — pricing via distributions[].purchase_price
        prix_vente_conseille: formData.prix_vente_conseille !== '' ? parseFloat(formData.prix_vente_conseille) : null,
        tva: formData.tva !== '' ? parseFloat(formData.tva) : null,
        
        // 5. LOGISTIQUE
        taille_boite: formData.taille_boite || null,
        poids_boite: formData.poids_boite !== '' ? parseFloat(formData.poids_boite) : null,
        
        // 6. DATES
        date_sortie_iso: formData.date_sortie_iso || null,
        // disponibilite: SUPPRIMÉ → distributions
        
        // 7. MARKETING
        visuels: Array.isArray(formData.visuels) 
          ? formData.visuels 
          : (typeof formData.visuels === 'string' && formData.visuels.trim() 
              ? formData.visuels.split(',').map(v => v.trim()).filter(v => v) 
              : []),
        atouts_1: formData.atouts_1 || null,
        atouts_2: formData.atouts_2 || null,
        atouts_3: formData.atouts_3 || null,
        pourquoi_referencer: formData.pourquoi_referencer || null,
        plan_communication_commercial: formData.plan_communication_commercial || null,
        video_url: formData.video_url || null,
        regle_jeu_url: formData.regle_jeu_url || null,
        
        // 8. SYSTÈME
        is_active: formData.is_active
      };

      if (editingProduct) {
        // Validate and save mechanics before product update
        if (distManagerRef.current?.validateAndSaveAllMechanics) {
          const ok = await distManagerRef.current.validateAndSaveAllMechanics();
          if (!ok) { setSaving(false); return; }
        }
        await api.put(`/products/${editingProduct.id}`, submitData);
        toast.success('Produit modifié avec succès');
      } else {
        const res = await api.post('/products', submitData);
        const newProductId = res.data?.id;
        toast.success('Produit créé avec succès');
        
        // Create pending publishers
        if (pendingPublishers.length > 0 && newProductId) {
          for (const pub of pendingPublishers) {
            try {
              await api.post(`/products/${newProductId}/publishers`, {
                product_id: newProductId,
                partner_id: pub.partner_id,
                role: 'publisher',
              });
            } catch (err) {
              console.error('Error creating publisher link:', err);
            }
          }
        }
        
        // Create pending distributions
        if (pendingDistributions.length > 0 && newProductId) {
          for (const dist of pendingDistributions) {
            try {
              const distPayload = {
                product_id: newProductId,
                partner_id: dist.partner_id,
                purchase_price: dist.purchase_price,
                supplier_reference: dist.supplier_reference,
                availability_status: dist.availability_status || 'unknown',
                minimum_commande: dist.minimum_commande,
                maximum_commande: dist.maximum_commande,
                multiple_commande: dist.multiple_commande,
                colisage: dist.colisage,
              };
              const distRes = await api.post(`/products/${newProductId}/distributions`, distPayload);
              const newDistId = distRes.data?.id;
              
              // Save promotional_mechanics if any
              if (dist.promotional_mechanics?.length > 0 && newDistId) {
                await api.put(`/products/${newProductId}/distributions/${newDistId}`, {
                  promotional_mechanics: dist.promotional_mechanics
                });
              }
            } catch (err) {
              console.error('Error creating distribution:', err);
            }
          }
        }
      }

      setShowDialog(false);
      resetDirty();
      setPendingPublishers([]);
      setPendingDistributions([]);
      setEditingProduct(null);
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // === ATTACH DISTRIBUTION (EAN rattachement avec mini-formulaire) ===
  const [attachTarget, setAttachTarget] = useState(null); // { id, intitule, code_ean, editeur, image }
  const [attachSubmitting, setAttachSubmitting] = useState(false);

  const handleAttachDistribution = (existingProductId) => {
    // Ouvrir le mini-formulaire au lieu de soumettre directement
    // On récupère les infos du produit depuis le lookup EAN dans ProductForm
    // Le productId est passé, on fetch les infos si nécessaire
    const fetchAndOpen = async () => {
      try {
        const res = await api.get(`/products/lookup-ean/${encodeURIComponent(
          products.find(p => p.id === existingProductId)?.code_ean || ''
        )}`);
        if (res.data.found) {
          setAttachTarget(res.data.product);
        } else {
          // Fallback: ouvrir avec les infos minimales
          setAttachTarget({ id: existingProductId, intitule: 'Produit', code_ean: '', editeur: '' });
        }
      } catch {
        setAttachTarget({ id: existingProductId, intitule: 'Produit', code_ean: '', editeur: '' });
      }
    };
    // Si on a les infos dans le state, utiliser directement
    fetchAndOpen();
  };

  const handleAttachDistributionFromForm = (existingProductId, lookupProduct) => {
    if (lookupProduct) {
      resetDirty(); // Reset dirty state AVANT fermeture pour éviter le GuardedDialog overlay
      setShowDialog(false);
      setTimeout(() => setAttachTarget(lookupProduct), 100);
    } else {
      handleAttachDistribution(existingProductId);
    }
  };

  const handleAttachSubmit = async (productId, distributionData) => {
    setAttachSubmitting(true);
    try {
      // D3: partner_id requis par le modèle — envoie activeOrg.org_id (backend force via JWT)
      distributionData.partner_id = partnerId;
      await api.post(`/products/${productId}/distributions`, distributionData);
      toast.success('Distribution créée avec succès');
      setAttachTarget(null);
      loadProducts();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('déjà associé')) {
        toast.error('Vous distribuez déjà ce produit');
      } else {
        toast.error(detail || 'Erreur lors de la création');
      }
    } finally {
      setAttachSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await api.delete(`/products/${deleteConfirm.id}`);
      toast.success('Produit supprimé');
      setDeleteConfirm(null);
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Obtenir le premier visuel du produit
  const getProductImage = (product) => {
    if (product.visuels && product.visuels.length > 0) {
      return product.visuels[0];
    }
    return null;
  };

  return (
    <DashboardWrapper>
      <div className="space-y-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold" data-testid="partner-products-title">
              Mes Produits
            </h1>
            <p className="text-muted-foreground mt-1">
              {products.length} produit{products.length > 1 ? 's' : ''} • Gérez votre catalogue
            </p>
          </div>
          {canCreateProducts && (
            <Button onClick={() => handleOpenDialog()} className="shrink-0" data-testid="create-product-button">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un produit
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par nom, éditeur, EAN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-products-input"
            />
          </div>
        </div>

        {/* Products Table/List */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Aucun produit ne correspond à votre recherche' : 'Aucun produit pour le moment'}
              </p>
              {!searchTerm && canCreateProducts && (
                <Button onClick={() => handleOpenDialog()} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer votre premier produit
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Image</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Désignation</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">Éditeur</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Prix</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell">Statut</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden lg:table-cell">Disponibilité</th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50" data-testid={`product-row-${product.id}`}>
                      {/* Image */}
                      <td className="px-4 py-3">
                        {getProductImage(product) ? (
                          <div className="w-12 h-12 bg-slate-50 rounded border flex items-center justify-center">
                            <img
                              src={getProductImage(product)}
                              alt={product.intitule}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/48?text=?';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center">
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </td>
                      
                      {/* Désignation */}
                      <td className="px-4 py-3">
                        <div className="font-medium truncate max-w-[200px]" title={product.intitule}>
                          {product.intitule}
                        </div>
                        {product.code_ean && (
                          <div className="text-xs text-muted-foreground font-mono">
                            {product.code_ean}
                          </div>
                        )}
                      </td>
                      
                      {/* Éditeur */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        {(() => { const re = resolveProduct(product).editors; return re.displayLabel || '-'; })()}
                      </td>
                      
                      {/* Prix */}
                      <td className="px-4 py-3">
                        <div className="font-medium">{(() => { const rp = resolveProduct(product).pricing; return rp.hasAnyPrice ? `${rp.price.toFixed(2)}€` : rp.priceLabel; })()}</div>
                        {product.prix_vente_conseille && (
                          <div className="text-xs text-muted-foreground">
                            PVC: {product.prix_vente_conseille.toFixed(2)}€
                          </div>
                        )}
                      </td>
                      
                      {/* Statut */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          product.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {product.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      
                      {/* Indicateur dérivé distribution-level (read-only, non source métier) */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          (() => {
                            const dists = product.distributions || [];
                            if (dists.length === 0) return 'bg-gray-100 text-gray-500';
                            if (dists.some(d => d.availability_status === 'available')) return 'bg-green-100 text-green-700';
                            if (dists.every(d => d.availability_status === 'unavailable')) return 'bg-red-100 text-red-700';
                            return 'bg-gray-100 text-gray-500';
                          })()
                        }`}>
                          {(() => {
                            const dists = product.distributions || [];
                            if (dists.length === 0) return 'N/C';
                            if (dists.some(d => d.availability_status === 'available')) return 'En stock';
                            if (dists.every(d => d.availability_status === 'unavailable')) return 'Indisponible';
                            return 'N/C';
                          })()}
                        </span>
                      </td>
                      
                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canEditProducts && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          )}
                          {canEditProducts && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm(product)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Dialog Form - Utilise le composant unifié ProductForm */}
        <GuardedDialog open={showDialog} onOpenChange={setShowDialog} isDirty={hasUnsavedChanges} className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                {editingProduct ? `Modifier: ${editingProduct.intitule}` : 'Ajouter un produit'}
              </DialogTitle>
            </DialogHeader>
            
            <ProductForm
              role="partenaire"
              formData={formData}
              onFormChange={handleFormChange}
              partners={[]}
              editingProduct={editingProduct}
              onSubmit={handleSubmit}
              onCancel={() => setShowDialog(false)}
              saving={saving}
              onAttachDistribution={handleAttachDistributionFromForm}
              editorsSlot={(
                <AdminOriginalPublisherManager 
                  productId={editingProduct?.id || null}
                  pendingPublishers={pendingPublishers}
                  onPendingChange={setPendingPublishers}
                />
              )}
              distributionsSlot={
                <AdminDistributionsManager
                  ref={distManagerRef}
                  productId={editingProduct?.id || null}
                  currentPartnerId={partnerId}
                  partners={partnerAsArray}
                  onPrimaryChange={(newPartnerId) => handleFormChange('partner_id', newPartnerId)}
                  pendingDistributions={!editingProduct ? pendingDistributions : undefined}
                  onPendingChange={!editingProduct ? setPendingDistributions : undefined}
                  onDirty={setDirty}
                  partnerMode={true}
                />
              }
            />
        </GuardedDialog>

        {/* Mini-formulaire rattachement distribution */}
        <DistributionAttachForm
          open={!!attachTarget}
          onClose={() => setAttachTarget(null)}
          product={attachTarget}
          onSubmit={handleAttachSubmit}
          submitting={attachSubmitting}
        />

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
              <AlertDialogDescription>
                Êtes-vous sûr de vouloir supprimer "{deleteConfirm?.intitule}" ? 
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardWrapper>
  );
};

export default PartnerProducts;
